import { UserType } from '@luminovo/api-contracts';
import { isPresent, isProductionEnvironment } from '@luminovo/commons';
import { toUserTypeTrait, useHttpQuery } from '@luminovo/data-stack';
import React from 'react';

import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';
import { useDialogContext, useDrawerContext, useIsIntercomLauncherHidden } from '@/shared/data/contexts/ModalContext';
import { useFeatureFlag } from '@/shared/permissions/featureFlags';

export const IntercomWidget: React.FunctionComponent = () => {
    const { user, tenant, organization, customer, userType, isClosedBetaTester, hasAccessToCustomerPortal } =
        useCurrentUserDetailsContext();
    const featureFlags = useFeatureFlag();
    const { isOpen: isDialogOpen } = useDialogContext();
    const { isOpen: isDrawerOpen } = useDrawerContext();
    const isLauncherForceHidden = useIsIntercomLauncherHidden();

    const { data } = useHttpQuery(
        'GET /my/intercom',
        {},
        {
            select: (res) => res.data,
        },
    );

    // Internal users see the launcher unless a drawer/dialog is open.
    // Customer and supplier users get Intercom for product tours, but never see the launcher.
    const isCustomerUser = userType === UserType.Customer;
    const isInternalUser = userType === UserType.Internal;
    const shouldBootIntercom = isPresent(data) && isProductionEnvironment();

    // Re-boot only on tenant switch. Re-booting on every render keeps
    // extending Intercom's session cookie (a JWT of user/company traits) until
    // it overflows the request header size limit — see COR-1625. `shutdown`
    // before each boot clears the previous tenant's cookies.
    React.useEffect(() => {
        if (!shouldBootIntercom) {
            return;
        }
        window.Intercom('shutdown');
        window.Intercom('boot', {
            app_id: window.INTERCOM_APP_ID,
            api_base: 'https://api-iam.eu.intercom.io',
            user_id: data.user_id,
            user_hash: data.hmac,
            email: user.email,
            name: `${user.first_name} ${user.last_name}`,
            language_override: user.user_language,
            user_type: toUserTypeTrait(userType),
            // Kept for backwards compatibility with existing Intercom audiences.
            // Prefer `user_type` for any new audience filters.
            is_from_customer: isCustomerUser,
            customer_name: customer?.name,
            has_access_to_customer_portal: hasAccessToCustomerPortal,
            // Customer and supplier users can't open negotiations, so for them the add-on is off.
            has_access_to_source_to_contract: isInternalUser && featureFlags.negotiations,
            is_closed_beta_tester: isClosedBetaTester,
            hide_default_launcher: !isInternalUser,
            company: {
                id: tenant,
                name: organization.name,
            },
        });

        return () => {
            window.Intercom('shutdown');
        };
    }, [shouldBootIntercom, tenant]);

    React.useEffect(() => {
        if (!shouldBootIntercom || !isInternalUser) {
            return;
        }
        window.Intercom('update', { hide_default_launcher: isDrawerOpen || isDialogOpen || isLauncherForceHidden });
    }, [shouldBootIntercom, isInternalUser, isDialogOpen, isDrawerOpen, isLauncherForceHidden]);

    return <></>;
};
